import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Save, ArrowLeft, AlertTriangle, CheckCircle2, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MANDATORY_FIELDS = ['name', 'address', 'city', 'contact_name', 'whatsapp_number', 'designation', 'can_influence_decisions'];

const designationOptions = ['Clinic Owner (Orthodontist)', 'Clinic Owning (Non-Orthodontist)', 'Consultant Orthodontist', 'Others'];

const isMissing = (val) => !val || val === 'TBD' || val === '';

const getMissingFields = (row) => {
  const missing = [];
  if (isMissing(row.name)) missing.push('Account Name');
  if (isMissing(row.address)) missing.push('Address');
  if (isMissing(row.city)) missing.push('City');
  if (isMissing(row.contact_name)) missing.push('Contact Name');
  if (isMissing(row.whatsapp_number)) missing.push('Phone Number');
  if (isMissing(row.designation)) missing.push('Designation');
  if (isMissing(row.can_influence_decisions)) missing.push('Can Influence Decisions');
  return missing;
};

const BulkEditAccounts = ({ onLogout }) => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all / incomplete
  const [savedRows, setSavedRows] = useState(new Set());

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(res.data);
      // Flatten accounts into editable rows
      const flat = res.data.map(acc => {
        const contact = acc.contacts?.[0] || {};
        return {
          id: acc.id,
          account_id: acc.account_id || '',
          name: acc.name || '',
          address: acc.address || '',
          city: acc.city || '',
          pincode: acc.pincode || '',
          current_sales_value: acc.current_sales_value || 0,
          email: contact.email || '',
          contact_name: contact.name || '',
          whatsapp_number: contact.whatsapp_number || '',
          designation: contact.designation || '',
          can_influence_decisions: contact.can_influence_decisions || '',
          contact_id: contact.id || '',
          _original: JSON.stringify({
            name: acc.name, address: acc.address, city: acc.city,
            pincode: acc.pincode, current_sales_value: acc.current_sales_value,
            contact_name: contact.name, whatsapp_number: contact.whatsapp_number,
            designation: contact.designation, can_influence_decisions: contact.can_influence_decisions,
            email: contact.email
          })
        };
      });
      setRows(flat);
    } catch (error) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (id, field, value) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
    setSavedRows(prev => { const s = new Set(prev); s.delete(id); return s; });
  };

  const isDirty = (row) => {
    const orig = JSON.parse(row._original);
    return (
      row.name !== orig.name || row.address !== orig.address ||
      row.city !== orig.city || row.pincode !== orig.pincode ||
      String(row.current_sales_value) !== String(orig.current_sales_value) ||
      row.contact_name !== orig.contact_name ||
      row.whatsapp_number !== orig.whatsapp_number ||
      row.designation !== orig.designation ||
      row.can_influence_decisions !== orig.can_influence_decisions ||
      row.email !== orig.email
    );
  };

  const handleSaveRow = async (row) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // Update account
      await axios.put(`${API}/accounts/${row.id}`, {
        name: row.name, address: row.address, city: row.city,
        pincode: row.pincode, current_sales_value: parseFloat(row.current_sales_value) || 0
      }, { headers });
      // Update contact if exists
      if (row.contact_id) {
        await axios.put(`${API}/accounts/${row.id}/contacts/${row.contact_id}`, {
          name: row.contact_name, whatsapp_number: row.whatsapp_number,
          designation: row.designation, can_influence_decisions: row.can_influence_decisions,
          email: row.email || null
        }, { headers });
      }
      // Update _original
      setRows(prev => prev.map(r => r.id === row.id ? {
        ...r,
        _original: JSON.stringify({
          name: r.name, address: r.address, city: r.city, pincode: r.pincode,
          current_sales_value: r.current_sales_value, contact_name: r.contact_name,
          whatsapp_number: r.whatsapp_number, designation: r.designation,
          can_influence_decisions: r.can_influence_decisions, email: r.email
        })
      } : r));
      setSavedRows(prev => new Set([...prev, row.id]));
      toast.success(`${row.name} saved!`);
    } catch (error) {
      toast.error(`Failed to save ${row.name}`);
    }
  };

  const handleSaveAll = async () => {
    const dirtyRows = filteredRows.filter(isDirty);
    if (dirtyRows.length === 0) { toast.info('No changes to save'); return; }
    setSaving(true);
    let saved = 0;
    for (const row of dirtyRows) {
      try { await handleSaveRow(row); saved++; } catch {}
    }
    setSaving(false);
    toast.success(`${saved} account${saved !== 1 ? 's' : ''} saved!`);
  };

  // Filter rows
  const filteredRows = rows.filter(row => {
    const matchSearch = !searchQuery ||
      row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.account_id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchFilter = filterMode === 'all' || getMissingFields(row).length > 0;
    return matchSearch && matchFilter;
  });

  const incompleteCount = rows.filter(r => getMissingFields(r).length > 0).length;
  const dirtyCount = filteredRows.filter(isDirty).length;

  const cellClass = (row, field) => {
    const missing = isMissing(row[field]);
    const base = "h-9 text-sm rounded border px-2 w-full focus:outline-none focus:ring-1 focus:ring-violet-400";
    return missing
      ? `${base} border-red-300 bg-red-50 text-red-800 placeholder-red-300`
      : `${base} border-slate-200 bg-white`;
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="p-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/accounts')}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-slate-500" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Edit Multiple Accounts
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {incompleteCount > 0
                  ? <span className="text-amber-600 font-medium">{incompleteCount} accounts have missing fields highlighted in red</span>
                  : <span className="text-emerald-600 font-medium">All accounts are complete ✓</span>
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {dirtyCount > 0 && (
              <span className="text-sm text-teal-700 font-medium">
                {dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}
              </span>
            )}
            <Button
              onClick={handleSaveAll}
              disabled={saving || dirtyCount === 0}
              className="bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl px-6 font-semibold"
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : `Save All Changes`}
            </Button>
          </div>
        </div>

        {/* Search + Filter */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by account name, city or ID..."
              className="pl-9 h-10 rounded-xl border-slate-200"
            />
          </div>
          <Select value={filterMode} onValueChange={setFilterMode}>
            <SelectTrigger className="h-10 w-52 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Accounts ({rows.length})</SelectItem>
              <SelectItem value="incomplete">Incomplete Only ({incompleteCount})</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mb-3 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-red-100 border border-red-300 inline-block" />
            Missing / required field
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-white border border-slate-200 inline-block" />
            Filled field
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-teal-100 border border-teal-300 inline-block" />
            Unsaved change
          </span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-teal-500 border-t-transparent" />
            <p className="text-slate-500 mt-4">Loading accounts...</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: '1400px' }}>
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-24">ID</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-48">Account Name *</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-56">Address *</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">City *</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-24">Pincode</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-32">Contact Name *</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-36">Phone Number *</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-36">Designation *</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">Email</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">Can Influence Decisions *</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">Sales Value</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-20">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="text-center py-12 text-slate-400">
                      No accounts found
                    </td>
                  </tr>
                ) : (
                  filteredRows.map(row => {
                    const missing = getMissingFields(row);
                    const dirty = isDirty(row);
                    const saved = savedRows.has(row.id);
                    return (
                      <tr
                        key={row.id}
                        className={`hover:bg-slate-50/50 transition-colors ${dirty ? 'bg-teal-50/30' : ''}`}
                      >
                        {/* Account ID */}
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/accounts/${row.id}`)}
                            className="text-xs font-mono text-teal-700 bg-teal-50 px-2 py-0.5 rounded hover:bg-teal-100 hover:underline cursor-pointer transition-colors"
                            title="Click to open account"
                          >
                            {row.account_id} ↗
                          </button>
                        </td>

                        {/* Account Name */}
                        <td className="px-3 py-2">
                          <Input
                            value={row.name}
                            onChange={e => updateRow(row.id, 'name', e.target.value)}
                            className={cellClass(row, 'name')}
                            placeholder="Clinic name *"
                          />
                        </td>

                        {/* Address */}
                        <td className="px-3 py-2">
                          <Input
                            value={row.address}
                            onChange={e => updateRow(row.id, 'address', e.target.value)}
                            className={cellClass(row, 'address')}
                            placeholder="Full address *"
                          />
                        </td>

                        {/* City */}
                        <td className="px-3 py-2">
                          <Input
                            value={row.city}
                            onChange={e => updateRow(row.id, 'city', e.target.value)}
                            className={cellClass(row, 'city')}
                            placeholder="City *"
                          />
                        </td>

                        {/* Pincode */}
                        <td className="px-3 py-2">
                          <Input
                            value={row.pincode}
                            onChange={e => updateRow(row.id, 'pincode', e.target.value)}
                            className="h-9 text-sm rounded border border-slate-200 bg-white px-2 w-full focus:outline-none focus:ring-1 focus:ring-violet-400"
                            placeholder="Pincode"
                          />
                        </td>

                        {/* Contact Name */}
                        <td className="px-3 py-2">
                          <Input
                            value={row.contact_name}
                            onChange={e => updateRow(row.id, 'contact_name', e.target.value)}
                            className={cellClass(row, 'contact_name')}
                            placeholder="Contact name *"
                          />
                        </td>

                        {/* Phone Number */}
                        <td className="px-3 py-2">
                          <Input
                            value={row.whatsapp_number}
                            onChange={e => updateRow(row.id, 'whatsapp_number', e.target.value)}
                            className={cellClass(row, 'whatsapp_number')}
                            placeholder="Phone number *"
                          />
                        </td>

                        {/* Designation */}
                        <td className="px-3 py-2">
                          <select
                            value={row.designation}
                            onChange={e => updateRow(row.id, 'designation', e.target.value)}
                            className={`h-9 text-sm rounded border px-2 w-full focus:outline-none focus:ring-1 focus:ring-violet-400 ${isMissing(row.designation) ? 'border-red-300 bg-red-50 text-red-800' : 'border-slate-200 bg-white'}`}
                          >
                            <option value="">Select *</option>
                            {designationOptions.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </td>

                        {/* Email */}
                        <td className="px-3 py-2">
                          <Input
                            value={row.email}
                            onChange={e => updateRow(row.id, 'email', e.target.value)}
                            className="h-9 text-sm rounded border border-slate-200 bg-white px-2 w-full focus:outline-none focus:ring-1 focus:ring-violet-400"
                            placeholder="Email (optional)"
                            type="email"
                          />
                        </td>

                        {/* Can Influence Decisions */}
                        <td className="px-3 py-2">
                          <select
                            value={row.can_influence_decisions}
                            onChange={e => updateRow(row.id, 'can_influence_decisions', e.target.value)}
                            className={`h-9 text-sm rounded border px-2 w-full focus:outline-none focus:ring-1 focus:ring-violet-400 ${isMissing(row.can_influence_decisions) ? 'border-red-300 bg-red-50 text-red-800' : 'border-slate-200 bg-white'}`}
                          >
                            <option value="">Select *</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                        </td>

                        {/* Sales Value */}
                        <td className="px-3 py-2">
                          <Input
                            value={row.current_sales_value}
                            onChange={e => updateRow(row.id, 'current_sales_value', e.target.value)}
                            className="h-9 text-sm rounded border border-slate-200 bg-white px-2 w-full focus:outline-none focus:ring-1 focus:ring-violet-400"
                            type="number"
                            min="0"
                          />
                        </td>

                        {/* Save / Status */}
                        <td className="px-3 py-2">
                          {saved && !dirty ? (
                            <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto" />
                          ) : dirty ? (
                            <button
                              onClick={() => handleSaveRow(row)}
                              className="text-xs bg-teal-700 text-white px-3 py-1.5 rounded-lg hover:bg-violet-700 font-medium transition-colors whitespace-nowrap"
                            >
                              Save
                            </button>
                          ) : missing.length > 0 ? (
                            <AlertTriangle className="h-4 w-4 text-amber-400 mx-auto" title={`Missing: ${missing.join(', ')}`} />
                          ) : (
                            <CheckCircle2 className="h-4 w-4 text-emerald-400 mx-auto" />
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Bottom Save Bar */}
        {dirtyCount > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-4 z-50">
            <span className="text-sm">{dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}</span>
            <Button
              onClick={handleSaveAll}
              disabled={saving}
              className="bg-gradient-to-r from-teal-600 to-emerald-400 text-white h-9 rounded-xl text-sm font-semibold"
            >
              <Save className="mr-1 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All'}
            </Button>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default BulkEditAccounts;
